import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { Target, Save, Loader2, AlertTriangle, CheckCircle, Info, Shield, Code, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const corePixelFields = [
  {
    key: "meta_pixel_id",
    label: "Meta Pixel ID",
    placeholder: "e.g. 123456789012345",
    description: "Facebook/Meta Pixel for retargeting and conversions. Found in Meta Events Manager.",
    category: "core",
  },
  {
    key: "google_tag_id",
    label: "Google Tag / GTM / GA4",
    placeholder: "e.g. GTM-XXXXXXX or G-XXXXXXXXXX",
    description: "Google Tag Manager container or GA4 measurement ID.",
    category: "core",
  },
  {
    key: "tiktok_pixel_id",
    label: "TikTok Pixel ID",
    placeholder: "e.g. CXXXXXXXXXXXXXXXXX",
    description: "TikTok Pixel for ad tracking. Found in TikTok Ads Manager > Events.",
    category: "core",
  },
  {
    key: "linkedin_insight_tag",
    label: "LinkedIn Insight Tag / Partner ID",
    placeholder: "e.g. 1234567",
    description: "LinkedIn Insight Tag for B2B retargeting.",
    category: "core",
  },
];

const extendedPixelFields = [
  {
    key: "microsoft_uet_tag",
    label: "Microsoft Ads UET Tag ID",
    placeholder: "e.g. 12345678",
    description: "Microsoft/Bing Ads Universal Event Tracking tag.",
    category: "extended",
  },
  {
    key: "vibe_ctv_pixel",
    label: "Vibe CTV Pixel / Script ID",
    placeholder: "Vibe pixel ID or tracking code",
    description: "Connected TV retargeting via Vibe.co. Paste your pixel ID.",
    category: "extended",
  },
  {
    key: "audiogo_tracking",
    label: "AudioGO Tracking Script/Pixel",
    placeholder: "AudioGO tracking ID or pixel code",
    description: "Streaming audio ad tracking via AudioGO.",
    category: "extended",
  },
];

const capiFields = [
  {
    key: "meta_capi_token",
    label: "Meta Conversions API Access Token",
    placeholder: "EAAxxxxxxx...",
    description: "Server-side token for Meta CAPI. Generate in Meta Events Manager > Settings.",
  },
  {
    key: "meta_capi_test_code",
    label: "Meta CAPI Test Event Code",
    placeholder: "TEST12345",
    description: "Test code for validating server-side events before going live.",
  },
  {
    key: "google_server_container",
    label: "Google Server-Side GTM URL",
    placeholder: "https://gtm.yourdomain.com",
    description: "Server-side Google Tag Manager endpoint URL.",
  },
];

const customScriptFields = [
  {
    key: "custom_header_script",
    label: "Custom Header Script",
    placeholder: "<script>...</script>",
    description: "Injected in <head>. Admin-only. Used for custom DSP pixels, visitor reveal, etc.",
    multiline: true,
  },
  {
    key: "custom_body_script",
    label: "Custom Body Script",
    placeholder: "<script>...</script>",
    description: "Injected before </body>. Admin-only. Use for tracking that must load after page content.",
    multiline: true,
  },
];

export default function DashboardPixels() {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pixels, setPixels] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showCapi, setShowCapi] = useState(false);
  const [showExtended, setShowExtended] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => { fetchListing(); }, []);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API}/listings/my`, { headers: getAuthHeader() });
      if (res.data?.pixels) {
        setPixels(res.data.pixels);
        if (res.data.pixels.meta_capi_token || res.data.pixels.google_server_container) setShowCapi(true);
        if (res.data.pixels.vibe_ctv_pixel || res.data.pixels.audiogo_tracking || res.data.pixels.microsoft_uet_tag) setShowExtended(true);
        if (res.data.pixels.custom_header_script || res.data.pixels.custom_body_script) setShowCustom(true);
      }
    } catch (err) {
      console.error("Failed to fetch listing:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setPixels(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.get(`${API}/listings/my`, { headers: getAuthHeader() });
      const slug = res.data?.slug || res.data?.id;
      if (!slug) {
        toast.error("No listing found. Create or claim a listing first.");
        setSaving(false);
        return;
      }
      await axios.patch(`${API}/listings/${slug}`, { pixels }, { headers: getAuthHeader() });
      toast.success("Pixels saved successfully!");
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save pixels");
    } finally {
      setSaving(false);
    }
  };

  const renderPixelField = (field) => (
    <div key={field.key}>
      <div className="flex items-center gap-2 mb-2">
        <label className="block text-sm font-medium text-[#E2E8F0]">{field.label}</label>
        {pixels[field.key] ? (
          <span className="text-xs px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] rounded flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Configured
          </span>
        ) : (
          <span className="text-xs px-1.5 py-0.5 bg-white/5 text-[#64748B] rounded">Not Set</span>
        )}
      </div>
      {field.multiline ? (
        <textarea
          value={pixels[field.key] || ""}
          onChange={(e) => handleChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          rows={4}
          className="w-full bg-white/5 border border-white/10 text-white placeholder:text-[#64748B] rounded-md p-3 font-mono text-sm focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] outline-none"
          data-testid={`pixel-${field.key}`}
        />
      ) : (
        <Input
          value={pixels[field.key] || ""}
          onChange={(e) => handleChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
          data-testid={`pixel-${field.key}`}
        />
      )}
      <p className="text-xs text-[#64748B] mt-1">{field.description}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allStatusFields = [...corePixelFields, ...extendedPixelFields];

  return (
    <div className="space-y-6" data-testid="dashboard-pixels">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Pixels, Retargeting & Lead Intelligence</h1>
          <p className="text-[#94A3B8] mt-1">Configure tracking pixels to retarget visitors and measure conversions across all platforms</p>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges || saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6] disabled:opacity-50" data-testid="save-pixels-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Pixels</>}
        </Button>
      </div>

      {/* Info Banner */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#7C3AED]/10 border border-[#7C3AED]/20 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-[#E2E8F0]">Pixels are automatically injected on your public listing page. All events fire through the dataLayer for GTM compatibility.</p>
          <p className="text-xs text-[#94A3B8] mt-1">Tracking fires only when visitor consent is granted. Events include deduplication IDs for server-side tracking.</p>
        </div>
      </motion.div>

      {/* Pixel Status Grid */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Pixel Status</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {allStatusFields.map((p) => (
            <div key={p.key} className={`p-3 rounded-lg border ${pixels[p.key] ? "bg-[#10B981]/10 border-[#10B981]/20" : "bg-white/5 border-white/5"}`} data-testid={`status-${p.key}`}>
              <p className="text-xs text-[#64748B] mb-1">{p.label}</p>
              <p className={`text-sm font-medium ${pixels[p.key] ? "text-[#10B981]" : "text-[#64748B]"}`}>
                {pixels[p.key] ? "Installed" : "Missing"}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Core Pixels */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Target className="w-5 h-5 text-[#7C3AED]" />
          Core Tracking Pixels
        </h2>
        <div className="space-y-6">
          {corePixelFields.map(renderPixelField)}
        </div>
      </motion.div>

      {/* Extended Pixels */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => setShowExtended(!showExtended)}
          className="flex items-center justify-between w-full"
          data-testid="toggle-extended-pixels"
        >
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-[#F59E0B]" />
            Extended Pixels (CTV, Audio, Microsoft)
          </h2>
          {showExtended ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
        </button>
        {showExtended && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-6 mt-6">
            {extendedPixelFields.map(renderPixelField)}
          </motion.div>
        )}
      </motion.div>

      {/* Server-Side Tracking */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <button
          onClick={() => setShowCapi(!showCapi)}
          className="flex items-center justify-between w-full"
          data-testid="toggle-capi"
        >
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
            Server-Side Tracking (CAPI)
          </h2>
          {showCapi ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
        </button>
        <p className="text-sm text-[#94A3B8] mt-2">Advanced: sends events server-side, bypassing ad blockers.</p>
        {showCapi && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-6 mt-6">
            {capiFields.map(renderPixelField)}
          </motion.div>
        )}
      </motion.div>

      {/* Custom Scripts (Admin Only) */}
      {isAdmin && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-[#0F172A] border border-red-500/20 rounded-xl p-6">
          <button
            onClick={() => setShowCustom(!showCustom)}
            className="flex items-center justify-between w-full"
            data-testid="toggle-custom-scripts"
          >
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Code className="w-5 h-5 text-red-400" />
              Custom Scripts
              <span className="text-xs px-2 py-0.5 bg-red-500/10 text-red-400 rounded-full">Admin Only</span>
            </h2>
            {showCustom ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
          </button>
          <p className="text-sm text-[#94A3B8] mt-2">Inject custom tracking scripts. Use for DSP pixels, visitor reveal, or custom integrations.</p>
          {showCustom && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-6 mt-6">
              <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 flex items-start gap-2">
                <Shield className="w-4 h-4 text-red-400 mt-0.5" />
                <p className="text-xs text-red-300">Custom scripts execute on client browsers. Only add trusted, validated code. Malicious scripts can compromise visitor security.</p>
              </div>
              {customScriptFields.map(renderPixelField)}
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Floating Save */}
      {hasChanges && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1E293B] border border-white/10 rounded-xl p-4 shadow-2xl flex items-center gap-4 z-50">
          <p className="text-white text-sm">Unsaved pixel changes</p>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="floating-save-pixels">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </motion.div>
      )}
    </div>
  );
}
